import React, { useState, useEffect, useMemo } from "react";
import { facultyDepartmentApi, baseUrl } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import PhotoUpload from "../../components/shared/PhotoUpload/PhotoUpload";
import "./FacultyDepartment.scss";

// Initial fallback faculties dataset
const DEFAULT_FACULTIES = [];

const defaultFacultyForm = {
  _id: null,
  name: "",
  slug: "",
  deanName: "",
  phone: "",
  email: "",
  description: "",
  Status: "Active",
  departments: [],
};

const generateSlug = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const FacultyDepartment = () => {
  const [faculties, setFaculties]           = useState(DEFAULT_FACULTIES);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState(""); // 'add-faculty' | 'edit-faculty' | 'manage-depts'
  const [formData, setFormData]             = useState(defaultFacultyForm);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [deptsList, setDeptsList]             = useState([]);
  const [deptFormVisible, setDeptFormVisible] = useState(false);
  const [editingDeptIndex, setEditingDeptIndex] = useState(null);
  const defaultDeptForm = {
    name: "", slug: "", about: "", description: "", headName: "",
    headDesignation: "Professor", headEmail: "", headPhone: "", headPhoto: "",
    thrustAreas: "", programmes: "", achievements: "", placementInfo: "",
    studyMaterial: "", facultyMembersList: "", courseOutlines: "", timeTable: "",
    contactAddress: "", contactPhone: "", contactEmail: "", status: "Active"
  };
  const [deptForm, setDeptForm] = useState(defaultDeptForm);
  const [searchText, setSearchText]         = useState("");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [notif, setNotif]                   = useState(null);
  const [selectedFile, setSelectedFile]     = useState(null);
  const [previewUrl, setPreviewUrl]         = useState(null);

  useEffect(() => {
    fetchFaculties();
  }, []);

  const showNotification = (msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  const fetchFaculties = async () => {
    try {
      setLoading(true);
      const res = await apiClient(facultyDepartmentApi);
      if (res.ok) {
        const result = await res.json();
        if (result.data && Array.isArray(result.data)) {
          setFaculties(result.data);
        }
      }
    } catch (err) {
      console.warn("[Admin UI] API fetch failed, using default data:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter faculties
  const filteredFaculties = useMemo(() => {
    return faculties.filter((f) => {
      const matchSearch =
        f.name.toLowerCase().includes(searchText.toLowerCase()) ||
        f.deanName?.toLowerCase().includes(searchText.toLowerCase()) ||
        f.slug?.toLowerCase().includes(searchText.toLowerCase());
      const matchStatus =
        filterStatus === "all" || (f.Status || "Active") === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [faculties, searchText, filterStatus]);

  // Modal Openers
  const openAddFaculty = () => {
    setFormData(defaultFacultyForm);
    setSelectedFaculty(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setModalType("add-faculty");
  };

  const openEditFaculty = (fac) => {
    setFormData({ ...fac });
    setSelectedFaculty(fac);
    setSelectedFile(null);
    setPreviewUrl(fac.deanImage || null);
    setModalType("edit-faculty");
  };

  const openManageDepts = (fac) => {
    setSelectedFaculty(fac);
    setDeptsList(fac.departments ? [...fac.departments] : []);
    setDeptFormVisible(false);
    setEditingDeptIndex(null);
    setDeptForm(defaultDeptForm);
    setModalType("manage-depts");
  };

  const closeModal = () => {
    setModalType("");
    setSelectedFaculty(null);
    setFormData(defaultFacultyForm);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, deanImage: "" }));
  };

  // Handle Faculty Form Changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "name") {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  // Save Faculty (Create or Update)
  const handleSaveFaculty = async (e) => {
    e.preventDefault();
    console.log("handleSaveFaculty called, formData:", formData);
    
    if (!formData.name || !formData.name.trim()) {
      showNotification("Faculty Name is required", "error");
      return;
    }

    const payload = {
      ...formData,
      slug: formData.slug || generateSlug(formData.name),
      route: `/academics/faculties/${formData.slug || generateSlug(formData.name)}`,
    };

    try {
      setLoading(true);
      const isEdit = !!formData._id;
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `${facultyDepartmentApi}/${formData._id}` : facultyDepartmentApi;

      const payloadToSend = { ...payload };
      if (!isEdit) {
        delete payloadToSend._id;
      }

      let res;
      if (selectedFile) {
        const dataToSend = new FormData();
        Object.keys(payloadToSend).forEach(key => {
          if (key !== "deanImage" && key !== "_id" && payloadToSend[key] !== null && payloadToSend[key] !== undefined) {
            if (key === "departments") {
               dataToSend.append(key, JSON.stringify(payloadToSend[key]));
            } else {
               dataToSend.append(key, payloadToSend[key]);
            }
          }
        });
        dataToSend.append("deanImage", selectedFile);
        res = await apiFormDataClient(url, { method, body: dataToSend });
      } else {
        res = await apiClient(url, { method, body: JSON.stringify(payloadToSend) });
      }

      if (res.ok) {
        showNotification(`Faculty "${payload.name}" saved successfully via API.`);
        fetchFaculties();
        closeModal();
      } else {
        const errorData = await res.json().catch(() => ({}));
        showNotification(`Failed to save: ${errorData.message || 'Unknown error'}`, "error");
      }
    } catch (err) {
      console.error(err);
      showNotification(`Failed to save: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const formatStudyMaterialToString = (studyMat) => {
    if (!studyMat) return "";
    if (typeof studyMat === "string") return studyMat;
    if (Array.isArray(studyMat)) {
      return studyMat
        .map(item => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            const title = item.title || item.name || "";
            const url = item.url || item.link || "";
            if (title && url && title !== url) return `${title}|${url}`;
            if (url) return url;
            if (title) return title;
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");
    }
    return "";
  };

  const parseStudyMaterialFromString = (str) => {
    if (!str) return [];
    if (Array.isArray(str)) return str;
    if (typeof str !== 'string') return [];

    return str
      .split("\n")
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return null;

        const pipeIndex = trimmed.indexOf("|");
        if (pipeIndex !== -1) {
          const title = trimmed.substring(0, pipeIndex).trim();
          const url = trimmed.substring(pipeIndex + 1).trim();
          return {
            title: title || url,
            url: url || title
          };
        } else {
          return {
            title: trimmed,
            url: trimmed
          };
        }
      })
      .filter(Boolean);
  };

  const formatFacultyMembersToString = (members) => {
    if (!members) return "";
    if (typeof members === "string") return members;
    if (Array.isArray(members)) {
      return members
        .map(m => {
          if (typeof m === "string") return m;
          if (m && typeof m === "object") {
            const name = m.name || "";
            const desig = m.designation || "";
            const email = m.email || "";
            const photo = m.photo || "";
            if (name || desig || email || photo) {
              return [name, desig, email, photo].join("|").replace(/\|+$/, "");
            }
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");
    }
    return "";
  };

  const parseFacultyMembersFromString = (str) => {
    if (!str) return [];
    if (Array.isArray(str)) return str;
    if (typeof str !== 'string') return [];
    return str
      .split("\n")
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        const parts = trimmed.split("|").map(p => p.trim());
        return {
          name: parts[0] || "Faculty Member",
          designation: parts[1] || "Professor",
          email: parts[2] || "",
          photo: parts[3] || ""
        };
      })
      .filter(Boolean);
  };

  const formatKeyValuePairToString = (items) => {
    if (!items) return "";
    if (typeof items === "string") return items;
    if (Array.isArray(items)) {
      return items
        .map(item => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            const name = item.name || item.title || "";
            const url = item.url || item.link || "";
            if (name && url && name !== url) return `${name}|${url}`;
            if (url) return url;
            if (name) return name;
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");
    }
    return "";
  };

  const parseKeyValuePairFromString = (str) => {
    if (!str) return [];
    if (Array.isArray(str)) return str;
    if (typeof str !== 'string') return [];
    return str
      .split("\n")
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        const pipeIndex = trimmed.indexOf("|");
        if (pipeIndex !== -1) {
          const title = trimmed.substring(0, pipeIndex).trim();
          const url = trimmed.substring(pipeIndex + 1).trim();
          return { name: title || url, title: title || url, url: url || title };
        }
        return { name: trimmed, title: trimmed, url: trimmed };
      })
      .filter(Boolean);
  };

  const handleGenericFileUpload = async (e, fieldName) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      showNotification(`Uploading "${file.name}"...`, "info");
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiFormDataClient(`${facultyDepartmentApi}/upload`, {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const resData = await response.json();
      const uploadedUrl = resData?.data?.url || resData?.url;

      if (!uploadedUrl) {
        throw new Error("No URL returned from server");
      }

      const cleanFileName = file.name.replace(/\.[^/.]+$/, "");

      setDeptForm(prev => {
        if (fieldName === "headPhoto") {
          return { ...prev, headPhoto: uploadedUrl };
        } else if (fieldName === "studyMaterial") {
          const title = prompt("Enter Title for uploaded Study Material:", cleanFileName) || cleanFileName;
          const newLine = `${title}|${uploadedUrl}`;
          const current = prev.studyMaterial ? prev.studyMaterial.trim() : "";
          return { ...prev, studyMaterial: current ? `${current}\n${newLine}` : newLine };
        } else if (fieldName === "facultyMembersList") {
          const name = prompt("Enter Faculty Member Name:", cleanFileName) || cleanFileName;
          const desig = prompt("Enter Designation:", "Professor") || "Professor";
          const email = prompt("Enter Email (optional):", "") || "";
          const newLine = `${name}|${desig}|${email}|${uploadedUrl}`;
          const current = prev.facultyMembersList ? prev.facultyMembersList.trim() : "";
          return { ...prev, facultyMembersList: current ? `${current}\n${newLine}` : newLine };
        } else if (fieldName === "courseOutlines") {
          const title = prompt("Enter Document Title for Course Outline:", cleanFileName) || cleanFileName;
          const newLine = `${title}|${uploadedUrl}`;
          const current = prev.courseOutlines ? prev.courseOutlines.trim() : "";
          return { ...prev, courseOutlines: current ? `${current}\n${newLine}` : newLine };
        } else if (fieldName === "timeTable") {
          const title = prompt("Enter Title for Time Table Schedule:", cleanFileName) || cleanFileName;
          const newLine = `${title}|${uploadedUrl}`;
          const current = prev.timeTable ? prev.timeTable.trim() : "";
          return { ...prev, timeTable: current ? `${current}\n${newLine}` : newLine };
        }
        return prev;
      });

      showNotification(`"${file.name}" uploaded successfully!`, "success");
    } catch (err) {
      console.error("File upload error:", err);
      showNotification(`Upload failed: ${err.message}`, "error");
    } finally {
      e.target.value = "";
    }
  };

  // Save Departments for Selected Faculty
  const handleOpenDeptForm = (dept = null, index = null) => {
    if (dept) {
      setDeptForm({
        ...defaultDeptForm,
        ...dept,
        thrustAreas: Array.isArray(dept.thrustAreas) ? dept.thrustAreas.join("\n") : "",
        programmes: Array.isArray(dept.programmes) ? dept.programmes.join("\n") : "",
        achievements: Array.isArray(dept.achievements) ? dept.achievements.join("\n") : "",
        studyMaterial: formatStudyMaterialToString(dept.studyMaterial),
        facultyMembersList: formatFacultyMembersToString(dept.facultyMembersList),
        courseOutlines: formatKeyValuePairToString(dept.courseOutlines),
        timeTable: formatKeyValuePairToString(dept.timeTable),
        contactAddress: dept.contact?.address || dept.address || "",
        contactPhone: dept.contact?.phone || "",
        contactEmail: dept.contact?.email || ""
      });
      setEditingDeptIndex(index);
    } else {
      setDeptForm(defaultDeptForm);
      setEditingDeptIndex(null);
    }
    setDeptFormVisible(true);
  };

  const handleCloseDeptForm = () => {
    setDeptFormVisible(false);
    setEditingDeptIndex(null);
    setDeptForm(defaultDeptForm);
  };

  const handleDeptFormChange = (e) => {
    const { name, value } = e.target;
    setDeptForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "name" && !prev.slug) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSaveSingleDept = () => {
    if (!deptForm.name.trim() || !deptForm.slug.trim()) {
      showNotification("Department Name and Slug are required", "error");
      return;
    }
    
    const cleanSlug = generateSlug(deptForm.slug);
    
    // Parse arrays
    const parseList = (str) => typeof str === 'string' ? str.split("\n").map(s => s.trim()).filter(Boolean) : [];

    const finalDept = {
      ...deptForm,
      slug: cleanSlug,
      thrustAreas: parseList(deptForm.thrustAreas),
      programmes: parseList(deptForm.programmes),
      achievements: parseList(deptForm.achievements),
      studyMaterial: parseStudyMaterialFromString(deptForm.studyMaterial),
      facultyMembersList: parseFacultyMembersFromString(deptForm.facultyMembersList),
      courseOutlines: parseKeyValuePairFromString(deptForm.courseOutlines),
      timeTable: parseKeyValuePairFromString(deptForm.timeTable),
      contact: {
        address: deptForm.contactAddress || "",
        phone: deptForm.contactPhone || "",
        email: deptForm.contactEmail || ""
      }
    };

    if (editingDeptIndex !== null) {
      setDeptsList(prev => prev.map((d, i) => i === editingDeptIndex ? finalDept : d));
      showNotification(`Department "${finalDept.name}" updated in list. Click "Save All Departments" to save to database.`);
    } else {
      setDeptsList(prev => [...prev, finalDept]);
      showNotification(`Department "${finalDept.name}" added to list. Click "Save All Departments" to save to database.`);
    }
    handleCloseDeptForm();
  };

  const handleRemoveDept = (index) => {
    setDeptsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveDepts = async () => {
    if (!selectedFaculty) return;

    const updatedFaculty = {
      ...selectedFaculty,
      departments: deptsList,
    };

    try {
      setLoading(true);
      const url = `${facultyDepartmentApi}/${selectedFaculty._id}`;
      const res = await apiClient(url, {
        method: "PUT",
        body: JSON.stringify(updatedFaculty),
      });

      if (res.ok) {
        showNotification(`Departments updated for "${selectedFaculty.name}".`);
        fetchFaculties();
        closeModal();
      } else {
        const errorData = await res.json().catch(() => ({}));
        showNotification(`Failed to update departments: ${errorData.message || 'Unknown error'}`, "error");
      }
    } catch (err) {
      console.error(err);
      showNotification(`Failed to update departments: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete Faculty
  const handleDeleteFaculty = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      setLoading(true);
      const res = await apiClient(`${facultyDepartmentApi}/${id}`, { method: "DELETE" });
      if (res.ok) {
        showNotification(`Faculty "${name}" deleted.`);
        fetchFaculties();
      } else {
        const errorData = await res.json().catch(() => ({}));
        showNotification(`Failed to delete: ${errorData.message || 'Unknown error'}`, "error");
      }
    } catch (err) {
      console.error(err);
      showNotification(`Failed to delete: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for stats
  const totalFaculties = faculties.length;
  const totalDepartments = faculties.reduce(
    (acc, f) => acc + (f.departments?.length || 0),
    0
  );
  const activeFaculties = faculties.filter((f) => (f.Status || "Active") === "Active").length;

  return (
    <div className="fd-page">
      {/* ── Header ── */}
      <header className="fd-header">
        <div className="fd-header-left">
          <div className="fd-header-icon">🏛️</div>
          <div>
            <div className="fd-breadcrumb">
              <span>Academics</span>
              <span>/</span>
              <strong className="fd-bc-active">Faculty &amp; Department Management</strong>
            </div>
            <h1>Faculty &amp; Department Management</h1>
            <p>Manage all college faculties, dean profiles, and their constituent departments.</p>
          </div>
        </div>

        <button className="fd-add-btn" onClick={openAddFaculty}>
          + Add New Faculty
        </button>
      </header>

      {/* Notification */}
      {notif && (
        <div className={`fd-notif fd-notif--${notif.type}`}>
          {notif.msg}
        </div>
      )}

      {/* ── Stats ── */}
      <div className="fd-stats">
        <div className="fd-stat-card">
          <div className="fd-stat-icon fd-stat-icon--blue">🏛️</div>
          <div>
            <div className="fd-stat-val">{totalFaculties}</div>
            <div className="fd-stat-lbl">Total Faculties</div>
          </div>
        </div>

        <div className="fd-stat-card">
          <div className="fd-stat-icon fd-stat-icon--purple">📚</div>
          <div>
            <div className="fd-stat-val">{totalDepartments}</div>
            <div className="fd-stat-lbl">Total Departments</div>
          </div>
        </div>

        <div className="fd-stat-card">
          <div className="fd-stat-icon fd-stat-icon--green">✅</div>
          <div>
            <div className="fd-stat-val">{activeFaculties}</div>
            <div className="fd-stat-lbl">Active Faculties</div>
          </div>
        </div>

        <div className="fd-stat-card">
          <div className="fd-stat-icon fd-stat-icon--amber">🌐</div>
          <div>
            <div className="fd-stat-val">100%</div>
            <div className="fd-stat-lbl">API Dynamic Sync</div>
          </div>
        </div>
      </div>

      {/* ── Toolbar / Controls ── */}
      <div className="fd-controls">
        <div className="fd-search">
          <span className="fd-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search faculty name, dean, or slug..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="fd-filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* ── Faculty Grid ── */}
      {loading && faculties.length === 0 ? (
        <div className="fd-loading">
          <div className="fd-spinner" />
          <p>Loading faculties &amp; departments...</p>
        </div>
      ) : (
        <div className="fd-grid">
          {filteredFaculties.map((fac) => (
            <div key={fac._id || fac.slug} className="fd-card">
              <div className="fd-card-header">
                <div className="fd-card-avatar">
                  {fac.name.charAt(0)}
                </div>
                <div className="fd-card-title">
                  <h3>{fac.name}</h3>
                  <span className="fd-card-slug">/{fac.slug}</span>
                </div>
                <span className={`fd-status-badge fd-status-${(fac.Status || "Active").toLowerCase()}`}>
                  {fac.Status || "Active"}
                </span>
              </div>

              <div className="fd-card-body">
                <div className="fd-meta-item">
                  <span className="fd-meta-label">Dean:</span>
                  <span className="fd-meta-val">{fac.deanName || "N/A"}</span>
                </div>
                <div className="fd-meta-item">
                  <span className="fd-meta-label">Phone:</span>
                  <span className="fd-meta-val">{fac.phone || "N/A"}</span>
                </div>
                <div className="fd-meta-item">
                  <span className="fd-meta-label">Email:</span>
                  <span className="fd-meta-val">{fac.email || "N/A"}</span>
                </div>

                <div className="fd-depts-preview">
                  <div className="fd-depts-header">
                    <span>Departments ({fac.departments?.length || 0})</span>
                  </div>
                  <div className="fd-depts-pills">
                    {fac.departments && fac.departments.length > 0 ? (
                      fac.departments.slice(0, 4).map((d, i) => (
                        <span key={i} className="fd-dept-pill">
                          {d.name}
                        </span>
                      ))
                    ) : (
                      <span className="fd-no-depts">No departments added</span>
                    )}
                    {fac.departments && fac.departments.length > 4 && (
                      <span className="fd-dept-pill-more">
                        +{fac.departments.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="fd-card-footer">
                <button
                  className="fd-btn fd-btn--secondary"
                  onClick={() => openManageDepts(fac)}
                >
                  📋 Departments ({fac.departments?.length || 0})
                </button>
                <button
                  className="fd-btn fd-btn--outline"
                  onClick={() => openEditFaculty(fac)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="fd-btn fd-btn--danger"
                  onClick={() => handleDeleteFaculty(fac._id, fac.name)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}

          {filteredFaculties.length === 0 && (
            <div className="fd-empty">
              <p>No faculties found matching your criteria.</p>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL: Add/Edit Faculty ── */}
      {(modalType === "add-faculty" || modalType === "edit-faculty") && (
        <div className="fd-modal-overlay" onClick={closeModal}>
          <div className="fd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-modal-header">
              <h2>{formData._id ? "Edit Faculty" : "Add New Faculty"}</h2>
              <button className="fd-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <form onSubmit={handleSaveFaculty} className="fd-modal-form" noValidate>
              <div className="fd-form-row">
                <div className="fd-form-group">
                  <label htmlFor="fd-name">Faculty Name *</label>
                  <input
                    id="fd-name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g. Faculty of Arts"
                    required
                  />
                </div>
                <div className="fd-form-group">
                  <label htmlFor="fd-slug">Slug (auto-generated)</label>
                  <input
                    id="fd-slug"
                    name="slug"
                    type="text"
                    value={formData.slug}
                    onChange={handleFormChange}
                    placeholder="e.g. arts"
                  />
                </div>
              </div>

              <div className="fd-form-row">
                <div className="fd-form-group">
                  <label htmlFor="fd-dean">Dean Name</label>
                  <input
                    id="fd-dean"
                    name="deanName"
                    type="text"
                    value={formData.deanName}
                    onChange={handleFormChange}
                    placeholder="e.g. Prof. Arvind Mohan"
                  />
                </div>
                <div className="fd-form-group">
                  <label htmlFor="fd-phone">Phone Number</label>
                  <input
                    id="fd-phone"
                    name="phone"
                    type="text"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="e.g. 9335913874"
                  />
                </div>
              </div>

              <div className="fd-form-row">
                <div className="fd-form-group">
                  <label htmlFor="fd-email">Dean Email</label>
                  <input
                    id="fd-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="e.g. dean_arts@lkouniv.ac.in"
                  />
                </div>
                <div className="fd-form-group">
                  <label htmlFor="fd-status">Status</label>
                  <select
                    id="fd-status"
                    name="Status"
                    value={formData.Status}
                    onChange={handleFormChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="fd-form-group">
                <label>Dean Photo</label>
                <div style={{ width: "fit-content" }}>
                  <PhotoUpload
                    value={previewUrl}
                    onChange={handlePhotoChange}
                    onRemove={handleRemovePhoto}
                    inputId="fdPhotoUpload"
                    shape="circle"
                  />
                </div>
              </div>

              <div className="fd-form-group">
                <label htmlFor="fd-description">Description</label>
                <textarea
                  id="fd-description"
                  name="description"
                  rows={5}
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter detailed description of the faculty..."
                />
              </div>

              <div className="fd-modal-actions">
                <button type="button" className="fd-btn fd-btn--ghost" onClick={closeModal} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="fd-btn fd-btn--primary" disabled={loading}>
                  {loading ? "Saving..." : (formData._id ? "Update Faculty" : "Save Faculty")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: Manage Departments ── */}
      {modalType === "manage-depts" && selectedFaculty && (
        <div className="fd-modal-overlay" onClick={closeModal}>
          <div className="fd-modal fd-modal--lg" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="fd-modal-header">
              <h2>Departments — {selectedFaculty.name}</h2>
              <button className="fd-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="fd-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {!deptFormVisible ? (
                <>
                  <div className="fd-add-dept-box" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button type="button" className="fd-btn fd-btn--primary" onClick={() => handleOpenDeptForm()}>
                      + Add New Department
                    </button>
                  </div>

                  <div className="fd-depts-list">
                    {deptsList.map((dept, idx) => (
                      <div key={idx} className="fd-dept-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="fd-dept-idx" style={{ fontWeight: '600', color: '#64748b' }}>{idx + 1}.</span>
                          <div className="fd-dept-info">
                            <strong style={{ fontSize: '1.1rem' }}>{dept.name}</strong>
                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                              <span className="fd-dept-slug">/{dept.slug}</span>
                              {dept.headName && <span style={{ marginLeft: '8px' }}>• Head: {dept.headName}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            className="fd-btn fd-btn--outline"
                            onClick={() => handleOpenDeptForm(dept, idx)}
                            title="Edit Department"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="fd-btn fd-btn--danger-icon"
                            onClick={() => handleRemoveDept(idx)}
                            title="Remove Department"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}

                    {deptsList.length === 0 && (
                      <div className="fd-empty-depts" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        No departments added to this faculty yet. Click "Add New Department" above.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="fd-dept-form">
                  <h3 style={{ marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                    {editingDeptIndex !== null ? "Edit Department" : "Add Department"}
                  </h3>
                  
                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Department Name *</label>
                      <input type="text" name="name" value={deptForm.name} onChange={handleDeptFormChange} placeholder="e.g. Computer Science" required />
                    </div>
                    <div className="fd-form-group">
                      <label>Slug *</label>
                      <input type="text" name="slug" value={deptForm.slug} onChange={handleDeptFormChange} placeholder="e.g. computer-science" required />
                    </div>
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Head Name</label>
                      <input type="text" name="headName" value={deptForm.headName} onChange={handleDeptFormChange} placeholder="e.g. Dr. A.K. Sharma" />
                    </div>
                    <div className="fd-form-group">
                      <label>Head Designation</label>
                      <input type="text" name="headDesignation" value={deptForm.headDesignation} onChange={handleDeptFormChange} placeholder="e.g. Professor" />
                    </div>
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Head Email</label>
                      <input type="email" name="headEmail" value={deptForm.headEmail} onChange={handleDeptFormChange} placeholder="e.g. head_cs@university.ac.in" />
                    </div>
                    <div className="fd-form-group">
                      <label>Head Phone</label>
                      <input type="text" name="headPhone" value={deptForm.headPhone} onChange={handleDeptFormChange} placeholder="e.g. 9876543210" />
                    </div>
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label>Head Photo URL</label>
                        <label style={{ fontSize: '0.8rem', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}>
                          📷 Upload Photo
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleGenericFileUpload(e, "headPhoto")} />
                        </label>
                      </div>
                      <input type="text" name="headPhoto" value={deptForm.headPhoto} onChange={handleDeptFormChange} placeholder="https://example.com/photo.jpg or click Upload Photo" />
                    </div>
                    <div className="fd-form-group">
                      <label>Status</label>
                      <select name="status" value={deptForm.status} onChange={handleDeptFormChange}>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="fd-form-group">
                    <label>About</label>
                    <textarea name="about" rows={2} value={deptForm.about} onChange={handleDeptFormChange} placeholder="Short about text..." />
                  </div>

                  <div className="fd-form-group">
                    <label>Description</label>
                    <textarea name="description" rows={3} value={deptForm.description} onChange={handleDeptFormChange} placeholder="Detailed description..." />
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Thrust Areas (one per line)</label>
                      <textarea name="thrustAreas" rows={4} value={deptForm.thrustAreas} onChange={handleDeptFormChange} placeholder="Area 1&#10;Area 2" />
                    </div>
                    <div className="fd-form-group">
                      <label>Programmes (one per line)</label>
                      <textarea name="programmes" rows={4} value={deptForm.programmes} onChange={handleDeptFormChange} placeholder="B.Sc. CS&#10;M.Sc. CS" />
                    </div>
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Achievements (one per line)</label>
                      <textarea name="achievements" rows={4} value={deptForm.achievements} onChange={handleDeptFormChange} placeholder="Achievement 1&#10;Achievement 2" />
                    </div>
                    <div className="fd-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label>Study Material (Title|URL per line)</label>
                        <label style={{ fontSize: '0.8rem', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}>
                          📁 Upload File (PDF/Doc/Excel/Img)
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt" style={{ display: 'none' }} onChange={(e) => handleGenericFileUpload(e, "studyMaterial")} />
                        </label>
                      </div>
                      <textarea name="studyMaterial" rows={4} value={deptForm.studyMaterial} onChange={handleDeptFormChange} placeholder="Notes|https://link.to/notes&#10;Syllabus|https://link.to/syllabus" />
                    </div>
                  </div>

                  <div className="fd-form-group">
                    <label>Placement Info (Training & Placement)</label>
                    <textarea name="placementInfo" rows={2} value={deptForm.placementInfo} onChange={handleDeptFormChange} placeholder="Placement details..." />
                  </div>

                  <div className="fd-form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Faculty Members (Name|Designation|Email|Photo URL per line)</label>
                      <label style={{ fontSize: '0.8rem', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}>
                        📷 Upload Member Photo
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleGenericFileUpload(e, "facultyMembersList")} />
                      </label>
                    </div>
                    <textarea name="facultyMembersList" rows={3} value={deptForm.facultyMembersList} onChange={handleDeptFormChange} placeholder="Dr. A.K. Sharma|Professor|sharma@university.ac.in|https://example.com/photo.jpg" />
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label>Course Outlines (Name|URL per line)</label>
                        <label style={{ fontSize: '0.8rem', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}>
                          📁 Upload File / PDF
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={(e) => handleGenericFileUpload(e, "courseOutlines")} />
                        </label>
                      </div>
                      <textarea name="courseOutlines" rows={3} value={deptForm.courseOutlines} onChange={handleDeptFormChange} placeholder="Syllabus 2025|https://example.com/syllabus.pdf" />
                    </div>
                    <div className="fd-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label>Time Table (Title|URL per line)</label>
                        <label style={{ fontSize: '0.8rem', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}>
                          📅 Upload Schedule / PDF
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={(e) => handleGenericFileUpload(e, "timeTable")} />
                        </label>
                      </div>
                      <textarea name="timeTable" rows={3} value={deptForm.timeTable} onChange={handleDeptFormChange} placeholder="Academic Schedule 2025|https://example.com/timetable.pdf" />
                    </div>
                  </div>

                  <div className="fd-form-group">
                    <label>Department Contact Address</label>
                    <input type="text" name="contactAddress" value={deptForm.contactAddress} onChange={handleDeptFormChange} placeholder="e.g. Building 3, Ground Floor, Main Campus" />
                  </div>

                  <div className="fd-form-row">
                    <div className="fd-form-group">
                      <label>Department Contact Phone</label>
                      <input type="text" name="contactPhone" value={deptForm.contactPhone} onChange={handleDeptFormChange} placeholder="e.g. 0522-2740001" />
                    </div>
                    <div className="fd-form-group">
                      <label>Department Contact Email</label>
                      <input type="email" name="contactEmail" value={deptForm.contactEmail} onChange={handleDeptFormChange} placeholder="e.g. dept_history@university.ac.in" />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                    <button type="button" className="fd-btn fd-btn--ghost" onClick={handleCloseDeptForm}>
                      Cancel
                    </button>
                    <button type="button" className="fd-btn fd-btn--primary" onClick={handleSaveSingleDept}>
                      Save Department to List
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="fd-modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1rem', marginTop: '1rem' }}>
              <button type="button" className="fd-btn fd-btn--ghost" onClick={closeModal} disabled={deptFormVisible}>
                Close
              </button>
              <button type="button" className="fd-btn fd-btn--primary" onClick={handleSaveDepts} disabled={deptFormVisible}>
                Save All Departments ({deptsList.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyDepartment;
